import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import {
  deleteUserMutation,
  editUserMutation as archiveUserMutation,
} from "QueryMutationFragment/CommonQueryMutationFragment/mutations";
import {
  getStudentFeedQueryV2 as getStudentFeedQuery,
  getStudentsWithoutYearGroupQuery,
  getTeacherFeedQuery,
} from "StudentSetup/modules/StudentQueries";
import {
  getStudentFamilyDetails,
  writeStudentFamiliesInCache,
} from "StudentSetup/modules/GraphqlHelpers";
import {
  removeChildForFamilyMemberMutation,
  unenrolStudentMutation,
  blockUnblockStudentMutation,
  bulkStudentBlockUnblockMutation,
  bulkPortfolioDownloadMutation,
  addUserMutation,
  editUserMutation,
} from "StudentSetup/modules/StudentMutations";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { logBulkActionActivity } from "Login/modules/LoginModule/LoginThunks";
import { logoutUserMutation } from "Login/modules/LoginMutation";
import {
  parseAdminMenuId,
  getUserCourseMapFiltersV2,
} from "Administrator/modules/Utils";
import ACLStore from "lib/aclStore";
import { createToast } from "SchoolEntityUpsertModal/components/DetailsSection/modules/DetailsSectionUtils";
import {
  getSelectedAcademicYearId,
  getToastActionLabel,
  getStudentQueryVariables,
} from "SchoolSetup/modules/Utils";
import { getCurriculumProgramLabel } from "Platform/modules/PlatformUtils";
import { getTableSortOrder } from "SchoolSetup/modules/SchoolSetupModule";
import { bulkUploadStudentWizardDataMutation } from "AppComponents/StudentWizardModal/modules/StudentWizardMutation";
import { dispatchError } from "Utils";
import {
  getOrganizationId,
  getCurriculumProgramIdFromCurrentAdminNavigationNode,
} from "modules/CommonSelectors";
import {
  getRefetchQueriesForStudentBlock,
  getStudentFilters,
  buildStudentClassUpdatePayload,
} from "StudentSetup/modules/StudentUtils";
import { getOrganizationCurriculumProgramsByAcademicYearQuery } from "Login/routes/Administrator/components/AcademicYearTransitionConfiguration/modules/Query";
import { getOrganizationCurriculumProgramsQuery } from "modules/CommonQuery";
import { getStudentCourseMapQueryFromCache } from "AppComponents/CourseClassManageModal/components/StudentClassesFeed/modules/StudentClassesFeedGraphqlHelpers";
import { getCurriculumFlagsForClass } from "AppComponents/CourseClassManageModal/components/AddClassesModal/modules/AddClassesModalUtils";
import { getIsMultipleHomeroomAdvisorsEnabled } from "Utils/ACLStoreUtils";

// Constants
export const NAME = "studentSetup";
export const CHANGE_SEARCH_TERM = "CHANGE_SEARCH_TERM" + " " + NAME;
export const INIT_SEARCH_TERM = "INIT_SEARCH_TERM" + " " + NAME;
export const UPDATE_FILTERS = "UPDATE_FILTERS" + " " + NAME;
export const UPDATE_BULK_ASSIGN_YEAR_GROUP_SEARCH_TEXT =
  "UPDATE_BULK_ASSIGN_YEAR_GROUP_SEARCH_TEXT" + " " + NAME;
export const UPDATE_BULK_ASSIGN_YEAR_GROUP_COURSES_FILTER =
  "UPDATE_BULK_ASSIGN_YEAR_GROUP_COURSES_FILTER" + " " + NAME;
export const UPDATE_BULK_ASSIGN_YEAR_GROUP_ARCHIVAL_STATE_FILTER =
  "UPDATE_BULK_ASSIGN_YEAR_GROUP_ARCHIVAL_STATE_FILTER" + " " + NAME;
export const SET_BASIC_DETAILS = "SET_BASIC_DETAILS" + " " + NAME;
export const SET_PERSONAL_DETAILS = "SET_PERSONAL_DETAILS" + " " + NAME;
export const SET_STUDENT_CLASS_MAPPING =
  "SET_STUDENT_CLASS_MAPPING" + " " + NAME;
export const RESET_MANAGE_MODAL = "RESET_MANAGE_MODAL" + " " + NAME;
export const SET_SELECTED_OR_EXCLUDED_STUDENTS =
  "SET_SELECTED_OR_EXCLUDED_STUDENTS" + " " + NAME;
export const SET_ARE_ALL_STUDENTS_SELECTED =
  "SET_ARE_ALL_STUDENTS_SELECTED" + " " + NAME;
export const SET_BULK_SELECTED_STUDENT_STATUS_COUNTS =
  "SET_BULK_SELECTED_STUDENT_STATUS_COUNTS" + " " + NAME;
export const RESET_BULK_ACTIONS_STATE = "RESET_BULK_ACTIONS_STATE" + " " + NAME;
export const RESET_AFFECTED_BULK_ACTIONS_STATE =
  "RESET_AFFECTED_BULK_ACTIONS_STATE" + " " + NAME;
export const SET_BULK_ACTIVITIES_POLLING_STATE =
  "SET_BULK_ACTIVITIES_POLLING_STATE" + " " + NAME;

export const updateFilters = data => {
  return {
    type: UPDATE_FILTERS,
    data,
  };
};

export const changeSearchTerm = value => {
  return { type: CHANGE_SEARCH_TERM, payload: value };
};

export const updateBulkAssignYearGroupSearchText = payload => {
  return {
    type: UPDATE_BULK_ASSIGN_YEAR_GROUP_SEARCH_TEXT,
    payload,
  };
};

export const updateBulkAssignYearGroupCoursesFilter = payload => {
  return {
    type: UPDATE_BULK_ASSIGN_YEAR_GROUP_COURSES_FILTER,
    payload,
  };
};

export const updateBulkAssignYearGroupArchivalStateFilter = payload => {
  return {
    type: UPDATE_BULK_ASSIGN_YEAR_GROUP_ARCHIVAL_STATE_FILTER,
    payload,
  };
};

export const setBasicDetails = data => {
  return {
    type: SET_BASIC_DETAILS,
    payload: data,
  };
};

export const setPersonalDetails = data => {
  return {
    type: SET_PERSONAL_DETAILS,
    payload: data,
  };
};

export const setStudentClassMapping = data => {
  return {
    type: SET_STUDENT_CLASS_MAPPING,
    payload: data,
  };
};

export const resetManageModal = () => {
  return {
    type: RESET_MANAGE_MODAL,
  };
};

export const setSelectedOrExcludedStudents = data => {
  return {
    type: SET_SELECTED_OR_EXCLUDED_STUDENTS,
    payload: data,
  };
};

export const setAreAllStudentsSelected = value => ({
  type: SET_ARE_ALL_STUDENTS_SELECTED,
  payload: value,
});

export const setBulkSelectedStudentStatusCounts = statusCounts => {
  return {
    type: SET_BULK_SELECTED_STUDENT_STATUS_COUNTS,
    payload: statusCounts,
  };
};

export const resetBulkActionsState = () => {
  return {
    type: RESET_BULK_ACTIONS_STATE,
  };
};

export const resetAffectedBulkActionsState = studentIds => ({
  type: RESET_AFFECTED_BULK_ACTIONS_STATE,
  payload: { studentIds: _.castArray(studentIds) },
});

export const setBulkActivitiesPollingState = isActive => ({
  type: SET_BULK_ACTIVITIES_POLLING_STATE,
  payload: { isActive },
});

export const isHomeroomEnabled = () =>
  ACLStore.can("FeatureFlag:EnableHomeroom");

// Helper function to trim text input fields from basicDetails
const trimBasicDetailsFields = basicDetails => {
  const fieldsToTrim = [
    "firstName",
    "middleName",
    "lastName",
    "email",
    "sourceId",
    "preferredName",
    "prefix",
    "suffix",
  ];

  const trimmedDetails = { ...basicDetails };

  _.forEach(fieldsToTrim, field => {
    if (_.isString(trimmedDetails[field])) {
      trimmedDetails[field] = _.trim(trimmedDetails[field]);
    }
  });

  return trimmedDetails;
};

const getStaffRelationMap = ({ basicDetails }) => {
  if (getIsMultipleHomeroomAdvisorsEnabled()) {
    const homeroomAdvisors = _.get(basicDetails, "homeroomAdvisors", []);
    return _.map(homeroomAdvisors, staffId => ({
      relation: "HOMEROOM",
      staffId,
    }));
  }

  return [
    {
      relation: "HOMEROOM",
      staffId: _.get(basicDetails, "homeroomAdvisor", null),
    },
  ];
};

const _getStaffFilter = ({ id }) => {
  const staffFilter = {
    ...(id ? { academicYearIds: id } : {}),
    relation: "HOMEROOM",
    archivalState: "ALL",
  };
  return staffFilter;
};
export const getStaffFilter = _.memoize(_getStaffFilter, JSON.stringify);

export const getCourseCountFilters = () => {
  return {
    taskType: "PULL_COURSES",
    taskStatus: "ARRIVED",
  };
};

const _getCurriculumList = ({ curriculumPrograms }) => {
  const curriculumList = _.map(curriculumPrograms, curriculum => ({
    label: getCurriculumProgramLabel({
      curriculumProgram: curriculum,
      shouldIncludeAcronym: true,
    }),
    value: curriculum.id,
    acronym: _.get(curriculum, "acronym"),
  }));
  return curriculumList;
};

export const getCurriculumList = _.memoize(
  ({ curriculumPrograms }) => _getCurriculumList({ curriculumPrograms }),
  ({ curriculumPrograms }) => {
    return JSON.stringify(
      _.map(curriculumPrograms, ({ id, acronym, name }) => ({
        id,
        acronym,
        name,
      }))
    );
  }
);

export const getCurriculumProgramId = state => {
  const { subChildId } = _.get(state, "administrator.routes", {});
  let { curriculumProgramId } = parseAdminMenuId(subChildId);

  const activeTab = _.get(state, "login.activeTab");

  /**
   * If we are opening classes via new admin portal, consider
   * new nodes only.
   */

  const isAdminNavigationV1Enabled = !_.isEmpty(
    _.get(state, "adminNavigation.nodes", [])
  );

  const isAccountSettingsTab = activeTab === "accountSettingsTab";
  if (isAccountSettingsTab && isAdminNavigationV1Enabled) {
    curriculumProgramId = getCurriculumProgramIdFromCurrentAdminNavigationNode(
      state
    );
  }

  const isSchoolSettingsTab =
    _.get(state, "login.activeTab") === "schoolSettingsTab";

  if (isSchoolSettingsTab) {
    curriculumProgramId = _.get(state, "platform.currentCurriculumProgram.id");
  }

  return curriculumProgramId;
};

export const getStudentCurriculum = ({ courses, yearGroup }) => {
  const course = _.head(courses);
  const yearGroupCurriculum = _.get(yearGroup, "curriculumProgram");
  const courseCurriculum = _.get(course, "curriculumProgram");
  return yearGroupCurriculum || courseCurriculum;
};

export const updateDPCourseTitleFromCourseMap = ({ courseMap }) => {
  return _.map(courseMap, ({ course, tags }) => {
    if (!_.get(course, "curriculumProgram.type") == "IB_DP") {
      return course;
    }
    const title = _.get(course, "title", "");
    const tagLabel = _.get(_.head(tags), "label", "");
    return { ...course, title, tagLabel };
  });
};

export const updateCourseTitle = ({ course, studentId }) => {
  const userMap = _.get(course, "userMap.edges", []);
  const student = _.find(userMap, ({ node }) => {
    const userId = _.get(node, "user.id");
    return _.isEqual(userId, studentId);
  });
  const studentTag = _.head(_.get(student, "node.tags", []));
  const tagLabel = _.get(studentTag, "label", "");
  const courseTitle = _.get(course, "title");
  const title = tagLabel ? `${courseTitle} (${tagLabel})` : courseTitle;
  return title;
};

export const createStudent = () => {
  return async (dispatch, getState) => {
    const state = getState();

    const { basicDetails, personalDetails, studentClassMapping } = _.get(
      state,
      "studentSetup",
      {}
    );

    const organizationId = getOrganizationId(state);

    const selectedAcademicYearId = getSelectedAcademicYearId(state);

    const { filters: studentFilters } = getStudentFilters(state);
    const sortedFieldsWithOrder = _.get(
      state,
      "schoolSetup.sortFields.students",
      {}
    );
    const { orderByDirection, orderBy } = getTableSortOrder({
      sortedFieldsWithOrder,
    });

    const isUsedForAcademicYearTransition = _.get(
      state,
      "academicYearTransition.isUsedForAcademicYearTransition",
      false
    );

    const organizationCurriculumProgramsForFutureAYRefetchQuery = {
      query: getOrganizationCurriculumProgramsByAcademicYearQuery,
      variables: {
        organizationId,
        academicYearId: selectedAcademicYearId,
        entityFilters: {
          entityTypes: ["COURSE", "STAFF", "STUDENT"],
          academicYearId: selectedAcademicYearId,
        },
      },
    };

    const refetchQueries = [
      {
        query: getStudentFeedQuery,
        variables: getStudentQueryVariables({
          organizationId,
          selectedAcademicYearId,
          orderByDirection,
          orderBy,
          staffFilter: getStaffFilter({
            id: getSelectedAcademicYearId(state),
          }),
          studentQueryFilters: studentFilters,
          academicYearIds: studentFilters.academicYearIds,
          includeFiltersWithoutSearch: true,
        }),
      },
      organizationCurriculumProgramsForFutureAYRefetchQuery,
    ];

    if (isUsedForAcademicYearTransition) {
      refetchQueries.push({
        query: getOrganizationCurriculumProgramsQuery,
        variables: {
          organizationId,
          academicYearId: selectedAcademicYearId,
          entityFilters: {
            entityTypes: ["COURSE", "STAFF", "STUDENT"],
            academicYearId: selectedAcademicYearId,
          },
        },
      });
    }

    const {
      dateOfBirth,
      primaryNationality,
      secondaryNationality,
      firstLanguage,
      secondLanguage,
      gender,
    } = personalDetails;

    const staffRelationMap = getStaffRelationMap({ basicDetails });

    const classList = _.get(studentClassMapping, "classList", []);
    const addedClassIds = _.map(classList, item => _.get(item, "id"));

    const courseTagMap = [];
    const classLevelsMap = [];

    _.forEach(classList, item => {
      const {
        isDP,
        isCourseBasedGrading,
        isLearningCoursesEnabled,
      } = getCurriculumFlagsForClass(item);

      if (isCourseBasedGrading || (isDP && !isLearningCoursesEnabled)) {
        const courseId = _.get(item, "id");
        const subjectLevelId = _.get(item, "levels[0].id", "");

        if (!_.isEmpty(courseId) && !_.isEmpty(subjectLevelId)) {
          courseTagMap.push({ courseId, subjectLevelId });
        }
      } else {
        classLevelsMap.push({
          classId: item.id,
          subjectWiseLevels: _.map(
            _.get(item, "levels", []),
            ({ levelId, subjectId }) => ({
              subjectId,
              levelId,
            })
          ),
        });
      }
    });

    const dob = dateOfBirth?.format("YYYY-MM-DD");
    const languages = _.compact([firstLanguage, secondLanguage]);

    const otherDetails = {
      ...(dob ? { dob } : {}),
      ...(primaryNationality ? { primaryNationality } : {}),
      ...(secondaryNationality ? { secondaryNationality } : {}),
      ...(languages.length ? { languages } : {}),
    };

    const trimmedBasicDetails = trimBasicDetailsFields(basicDetails);

    const variables = {
      ...trimmedBasicDetails,
      otherDetails,
      userType: "student",
      ...(gender ? { gender } : {}),
      courseTagMap,
      classLevelsMap,
      courses: addedClassIds,
      ...(staffRelationMap?.[0]?.staffId ? { staffRelationMap } : {}),
      mergeUserWithSameEmail: false,
      academicYearId: selectedAcademicYearId,
    };

    try {
      const response = await client.mutate({
        mutation: addUserMutation,
        variables,
        awaitRefetchQueries: true,
        refetchQueries: refetchQueries,
      });

      const result = _.get(response, "data.platform.addUserV2", {});
      const { isSuccess, warning, user } = result;

      const warningCode = _.get(warning, "message", "");
      const code = _.get(warning, "code", "");

      if (code === "TD/SIGNUP/003") {
        dispatch(
          setToastMsg({
            msg: "project:multi_access_identity_not_found_error_msg",
            type: "error",
          })
        );
        return;
      }

      const emailAlreadyExists = _.isEqual(
        warningCode,
        "USER_EMAIL_ALREADY_EXISTS_IN_ORGANIZATION"
      );
      const uidAlreadyExists = _.isEqual(warningCode, "SOURCED_ID_NOT_UNIQUE");

      if (isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:name_created_successfully",
            locale_params: [
              {
                key: "name",
                value: "common:student",
              },
            ],
            type: "success",
          })
        );
      }

      return {
        isSuccess,
        code: warningCode,
        errorCode: code,
        data: user,
        emailAlreadyExists,
        uidAlreadyExists,
      };
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};

export const updateStudent = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const { basicDetails, personalDetails, studentClassMapping } = _.get(
      state,
      "studentSetup",
      {}
    );

    const organizationId = getOrganizationId(state);

    const selectedAcademicYearId = getSelectedAcademicYearId(state);

    const { filters: studentFilters } = getStudentFilters(state);
    const sortedFieldsWithOrder = _.get(
      state,
      "schoolSetup.sortFields.students",
      {}
    );
    const { orderByDirection, orderBy } = getTableSortOrder({
      sortedFieldsWithOrder,
    });

    const refetchQueries = [
      {
        query: getStudentFeedQuery,
        variables: getStudentQueryVariables({
          organizationId,
          selectedAcademicYearId,
          orderByDirection,
          orderBy,
          staffFilter: getStaffFilter({
            id: getSelectedAcademicYearId(state),
          }),
          studentQueryFilters: studentFilters,
          academicYearIds: studentFilters.academicYearIds,
          includeFiltersWithoutSearch: true,
        }),
      },
    ];

    const {
      dateOfBirth,
      primaryNationality,
      secondaryNationality,
      firstLanguage,
      secondLanguage,
      gender,
    } = personalDetails;

    const academicYearIds = [selectedAcademicYearId];
    const selectedStudentId = _.get(basicDetails, "id", "");
    const userCourseMapFilters = {
      archivalState: "ALL",
      academicYearIds,
    };
    const courseGradeFilters = {
      academicYearId: selectedAcademicYearId,
    };

    const originalStudentCourseMap = getStudentCourseMapQueryFromCache({
      id: selectedStudentId,
      userCourseMapFilters,
      courseGradeFilters,
    });

    const {
      addedClassLevelsMap,
      removedClassLevelsMap,
      addedCourses,
      removedCourses,
    } = buildStudentClassUpdatePayload(
      originalStudentCourseMap,
      studentClassMapping
    );

    const classList = _.get(studentClassMapping, "classList", []);

    const isClassCourseTagBased = classItem => {
      const {
        isDP,
        isCourseBasedGrading,
        isLearningCoursesEnabled,
      } = getCurriculumFlagsForClass(classItem);
      return isCourseBasedGrading || (isDP && !isLearningCoursesEnabled);
    };

    const courseTagMap = [];
    _.forEach(classList, item => {
      if (!isClassCourseTagBased(item)) return;

      const courseId = _.get(item, "id", "");
      const subjectLevelId = _.get(item, "levels[0].id", "");

      if (!_.isEmpty(courseId) && !_.isEmpty(subjectLevelId)) {
        courseTagMap.push({ courseId, subjectLevelId });
      }
    });

    const originalCourseById = _.keyBy(
      originalStudentCourseMap,
      item => item?.course?.id
    );

    const filteredAddedClassLevelsMap = _.filter(
      addedClassLevelsMap,
      ({ classId }) => {
        const classItem =
          _.find(classList, { id: classId }) ||
          originalCourseById[classId]?.course;
        return classItem && !isClassCourseTagBased(classItem);
      }
    );

    const filteredRemovedClassLevelsMap = _.filter(
      removedClassLevelsMap,
      ({ classId }) => {
        const classItem =
          _.find(classList, { id: classId }) ||
          originalCourseById[classId]?.course;
        return classItem && !isClassCourseTagBased(classItem);
      }
    );

    const staffRelationMap = getStaffRelationMap({ basicDetails });

    const dob = dateOfBirth?.format("YYYY-MM-DD");
    const languages = _.compact([firstLanguage, secondLanguage]);

    const trimmedBasicDetails = trimBasicDetailsFields(basicDetails);

    const variables = {
      ..._.omit(trimmedBasicDetails, ["isArchived"]),
      otherDetails: {
        ...(dob ? { dob } : { dob: null }),
        ...(primaryNationality ? { primaryNationality } : {}),
        ...(secondaryNationality ? { secondaryNationality } : {}),
        ...(languages.length ? { languages } : {}),
      },
      userType: "student",
      courseTagMap,
      addedClassLevelsMap: filteredAddedClassLevelsMap,
      removedClassLevelsMap: filteredRemovedClassLevelsMap,
      staffRelationMap,
      mergeUserWithSameEmail: false,
      academicYearId: selectedAcademicYearId,
      ...(gender ? { gender } : {}),
      addedCourses,
      removedCourses,
      ...(staffRelationMap[0]?.staffId ? { staffRelationMap } : {}),
    };

    try {
      const response = await client.mutate({
        mutation: editUserMutation,
        variables,
        awaitRefetchQueries: true,
        refetchQueries: refetchQueries,
      });

      const result = _.get(response, "data.platform.updateUserV2", {});
      const { isSuccess, warning, user } = result;
      const warningCode = _.get(warning, "message", "");
      const code = _.get(warning, "code", "");

      const emailAlreadyExists = _.isEqual(
        warningCode,
        "USER_EMAIL_ALREADY_EXISTS_IN_ORGANIZATION"
      );
      const uidAlreadyExists = _.isEqual(warningCode, "SOURCED_ID_NOT_UNIQUE");

      if (isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:entity_updated_successfully",
            locale_params: [
              {
                key: "entity",
                value: "common:student",
              },
            ],
            type: "success",
          })
        );
      }

      return {
        isSuccess,
        code: warningCode,
        errorCode: code,
        data: user,
        emailAlreadyExists,
        uidAlreadyExists,
      };
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};

export const deleteStudent = ({ studentId, programmeType, t }) => {
  return (dispatch, getState) => {
    const state = getState();
    const organizationId = state.login.userInfo.org_id;
    const filters = state.studentSetup.filters;

    const sortedFieldsWithOrder = state.schoolSetup.sortFields.students;

    const { orderByDirection, orderBy } = getTableSortOrder({
      sortedFieldsWithOrder,
    });

    const staffFilter = getStaffFilter({
      id: getSelectedAcademicYearId(state),
    });

    const selectedAcademicYearId = getSelectedAcademicYearId(state);
    const userCourseMapFilters = getUserCourseMapFiltersV2({
      archivalState: "ALL",
    });

    const studentQueryFilters = getStudentFilters(state);

    client
      .mutate({
        mutation: deleteUserMutation,
        variables: {
          id: studentId,
        },
        refetchQueries: [
          {
            query: getStudentFeedQuery,
            variables: getStudentQueryVariables({
              organizationId,
              selectedAcademicYearId,
              userCourseMapFilters,
              orderByDirection,
              orderBy,
              staffFilter,
              studentQueryFilters: studentQueryFilters?.filters,
              academicYearIds: filters.academicYears,
              includeFiltersWithoutSearch: true,
            }),
          },
        ],
      })
      .then(() => {
        dispatch(
          createToast(
            programmeType,
            "STUDENT",
            getToastActionLabel({ action: "DELETE", t }),
            t
          )
        );
      })
      .catch(() => {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      });
  };
};

export const unenrolStudent = ({
  studentId,
  programmeType,
  t,
  selectedAcademicYearId,
  curriculumProgramId,
}) => {
  return (dispatch, getState) => {
    const state = getState();
    const organizationId = state.login.userInfo.org_id;

    const sortedFieldsWithOrder = state.schoolSetup.sortFields.students;

    const { orderByDirection, orderBy } = getTableSortOrder({
      sortedFieldsWithOrder,
    });

    const filters = state.studentSetup.filters;

    const staffFilter = getStaffFilter({
      id: getSelectedAcademicYearId(state),
    });
    const userCourseMapFilters = getUserCourseMapFiltersV2({
      archivalState: "ALL",
      academicYearIds: [selectedAcademicYearId],
    });
    const studentQueryFilters = getStudentFilters(state);
    client
      .mutate({
        mutation: unenrolStudentMutation,
        variables: {
          id: studentId,
          academicYearId: selectedAcademicYearId,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getStudentFeedQuery,
            variables: getStudentQueryVariables({
              organizationId,
              selectedAcademicYearId,
              userCourseMapFilters,
              orderByDirection,
              orderBy,
              staffFilter,
              studentQueryFilters: studentQueryFilters?.filters,
              academicYearIds: filters.academicYears,
              includeFiltersWithoutSearch: true,
            }),
          },
        ],
      })
      .then(() => {
        dispatch(
          createToast(
            programmeType,
            "STUDENT",
            getToastActionLabel({ action: "UNENROL", t }),
            t
          )
        );
      })
      .catch(() => {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      });
  };
};

export const logoutStudentFromDevice = ({ studentId }) => {
  return async dispatch => {
    try {
      const result = await client.mutate({
        mutation: logoutUserMutation,
        variables: {
          input: {
            id: studentId,
            logoutFromAllDevices: true,
          },
        },
      });

      const isSuccess = _.get(
        result,
        "data.platform.logoutUser.isSuccess",
        false
      );

      if (isSuccess) {
        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:entity_logged_out_successfully",
            locale_params: [{ key: "entity", value: "common:student" }],
          })
        );
      } else {
        dispatch(
          setToastMsg({
            type: "warning",
            msg: "toastMsgs:something_went_wrong",
          })
        );
      }
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};

export const archiveStudent = ({
  studentId,
  isArchived,
  programmeType,
  t,
  withdrawalDate,
}) => {
  const label = !isArchived
    ? getToastActionLabel({ action: "ARCHIVE", t })
    : getToastActionLabel({ action: "RESTORED", t });
  return (dispatch, getState) => {
    const state = getState();
    const organizationId = state.login.userInfo.org_id;
    const sortedFieldsWithOrder = state.schoolSetup.sortFields.students;

    const { orderByDirection, orderBy } = getTableSortOrder({
      sortedFieldsWithOrder,
    });

    const filters = state.studentSetup.filters;

    const selectedAcademicYearId = getSelectedAcademicYearId(state);

    const academicYearIds = selectedAcademicYearId
      ? [selectedAcademicYearId]
      : [];

    const userCourseMapFilters = getUserCourseMapFiltersV2({
      archivalState: "ALL",
      academicYearIds,
    });

    const staffFilter = getStaffFilter({
      id: getSelectedAcademicYearId(state),
    });

    const studentQueryFilters = getStudentFilters(state);

    return client
      .mutate({
        mutation: archiveUserMutation,
        variables: {
          id: studentId,
          isArchived: !isArchived,
          academicYearId: selectedAcademicYearId,
          isBlocked: false, // Reset the block status when archiving a blocked student
          ...(!isArchived && withdrawalDate ? { withdrawalDate } : {}),
        },
        refetchQueries: [
          {
            query: getStudentFeedQuery,
            variables: getStudentQueryVariables({
              organizationId,
              selectedAcademicYearId,
              userCourseMapFilters,
              orderByDirection,
              orderBy,
              staffFilter,
              studentQueryFilters: studentQueryFilters?.filters,
              academicYearIds: filters.academicYears,
              includeFiltersWithoutSearch: true,
            }),
          },
        ],
      })
      .then(() => {
        dispatch(createToast(programmeType, "STUDENT", label, t));
      })
      .catch(() => {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      });
  };
};

export const removeFamilyMember = ({ studentId, familyMemberId, t }) => {
  return async (dispatch, getState) => {
    const studentData = getStudentFamilyDetails({ studentId });
    try {
      await client.mutate({
        mutation: removeChildForFamilyMemberMutation,
        variables: {
          studentId: studentId,
          parentId: familyMemberId,
        },
        optimisticResponse: {
          __typename: "Mutation",
          platform: {
            __typename: "PlatformMutations",
            removeChildForFamilyMember: true,
          },
        },
        update: (
          cache,
          {
            data: {
              platform: { removeChildForFamilyMember },
            },
          }
        ) => {
          if (removeChildForFamilyMember) {
            const studentData = getStudentFamilyDetails({ studentId });
            setTimeout(() => {
              writeStudentFamiliesInCache({
                studentId,
                data: {
                  ...studentData,
                  families: _.filter(
                    _.get(studentData, "families", []),
                    item => _.get(item, "familyMember.id", "") != familyMemberId
                  ),
                },
              });
            });
          }
        },
      });
      dispatch(
        setToastMsg({
          type: "success",
          msg: "toastMsgs:successfully_action_with_label",
          locale_params: [
            { key: "action", value: t("toastMsgs:removed"), isPlainText: true },
            {
              key: "label",
              value: _.toLower(t("common:family_member")),
              isPlainText: true,
            },
          ],
        })
      );
    } catch (e) {
      setTimeout(() => {
        writeStudentFamiliesInCache({
          studentId,
          data: studentData,
        });
      });
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const bulkUploadStudentWizardData = ({
  input,
  refetchQueryVariables,
}) => {
  return async (dispatch, getState) => {
    try {
      // Getting Necessary Variables for refetching studentFeed query //////
      const state = getState();
      const organizationId = getOrganizationId(state);
      const selectedAcademicYearId = getSelectedAcademicYearId(state);
      const userCourseMapFilters = getUserCourseMapFiltersV2({
        archivalState: "ALL",
      });

      const sortedFieldsWithOrder = state.schoolSetup.sortFields.students;

      const { orderByDirection, orderBy } = getTableSortOrder({
        sortedFieldsWithOrder,
      });

      const staffFilter = getStaffFilter({
        id: selectedAcademicYearId,
      });
      const filters = state.studentSetup.filters;
      const studentQueryFilters = getStudentFilters(state);

      const result = await client.mutate({
        mutation: bulkUploadStudentWizardDataMutation,
        variables: { input },
        refetchQueries: [
          {
            query: getStudentsWithoutYearGroupQuery,
            variables: refetchQueryVariables,
          },
          {
            query: getStudentFeedQuery,
            variables: getStudentQueryVariables({
              organizationId,
              selectedAcademicYearId,
              userCourseMapFilters,
              orderByDirection,
              orderBy,
              staffFilter,
              studentQueryFilters: studentQueryFilters?.filters,
              academicYearIds: filters.academicYears,
              includeFiltersWithoutSearch: true,
            }),
          },
        ],
      });
      const isSuccess = _.get(
        result,
        "data.platform.bulkUploadStudentWizardData",
        false
      );

      const toastType = isSuccess ? "success" : "warning";
      const toastMsg = isSuccess
        ? "toastMsgs:students_uploaded_successfully"
        : "toastMsgs:something_went_wrong";

      dispatch(
        setToastMsg({
          type: toastType,
          msg: toastMsg,
        })
      );

      return { isSuccess };
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};

export const blockStudent = ({
  studentId,
  isBlocked,
  blockMessage = "",
  programmeType,
  t,
}) => {
  const label = isBlocked
    ? getToastActionLabel({ action: "BLOCKED", t })
    : getToastActionLabel({ action: "UNBLOCKED", t });

  return async (dispatch, getState) => {
    try {
      const state = getState();

      const refetchQueries = getRefetchQueriesForStudentBlock({
        state,
      });

      const result = await client.mutate({
        mutation: blockUnblockStudentMutation,
        variables: {
          input: {
            id: studentId,
            isBlocked,
            blockMessage,
          },
        },
        refetchQueries,
      });

      const isSuccess = _.get(
        result,
        "data.platform.updateUserV2.isSuccess",
        false
      );

      if (isSuccess) {
        dispatch(createToast(programmeType, "STUDENT", label, t));
      } else {
        dispatch(
          setToastMsg({
            type: "warning",
            msg: "toastMsgs:something_went_wrong",
          })
        );
      }
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};

export const bulkPortfolioDownload = input => {
  const portfolioDownloadInput = {
    input: input,
  };
  return async (dispatch, getState) => {
    try {
      const result = await client.mutate({
        mutation: bulkPortfolioDownloadMutation,
        variables: portfolioDownloadInput,
      });
      const isSuccess = _.get(
        result,
        "data.documentation.bulkGenerateStudentPortfolio.isSuccess",
        false
      );

      if (isSuccess) {
        dispatch(
          setToastMsg({
            type: "info",
            msg: "toastMsgs:bulk_portfolio_download_info_message",
          })
        );
      } else {
        dispatch(
          setToastMsg({
            type: "warning",
            msg: "toastMsgs:something_went_wrong",
          })
        );
      }
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};

export const bulkBlockStudents = ({
  blockMessage = "",
  isBlocked,
  studentIds,
  allStudentMapInput,
  shouldRefetchQueries = true,
  activityLogModule = null,
  t,
}) => {
  const label = isBlocked
    ? getToastActionLabel({ action: "BLOCKED", t })
    : getToastActionLabel({ action: "UNBLOCKED", t });

  return async (dispatch, getState) => {
    try {
      const state = getState();

      const refetchQueries = shouldRefetchQueries
        ? getRefetchQueriesForStudentBlock({
            state,
          })
        : [];

      const result = await client.mutate({
        mutation: bulkStudentBlockUnblockMutation,
        variables: {
          input: {
            studentIds,
            isBlocked,
            blockMessage,
            ...(!_.isEmpty(allStudentMapInput) && { allStudentMapInput }),
          },
        },
        refetchQueries,
      });

      const isSuccess = _.get(
        result,
        "data.platform.bulkStudentBlockUnblock.isSuccess",
        false
      );

      const studentsCount = _.get(
        result,
        "data.platform.bulkStudentBlockUnblock.updatedUsersCount",
        0
      );

      if (isSuccess) {
        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:students_with_count_and_label_successfully",
            locale_params: [
              {
                key: "count",
                value: studentsCount,
                isPlainText: true,
              },
              {
                key: "label",
                value: label,
                isPlainText: true,
              },
            ],
          })
        );
        if (activityLogModule) {
          logBulkActionActivity({
            input: {
              module: activityLogModule,
              operation: "UPDATE",
              activityType: "BULK_ACTION",
              activityMessage: isBlocked
                ? "BLOCKED_STUDENTS_IN_BULK"
                : "UNBLOCKED_STUDENTS_IN_BULK",
              description: `${isBlocked ? "Blocked" : "Unblocked"} ${
                studentIds.length
              } students in bulk.`,
              entityType: "STUDENT",
              metadata: {
                entityType: "STUDENT",
                entityIds: studentIds,
                count: studentIds.length,
                academicYearId:
                  _.first(
                    _.get(allStudentMapInput, "includedAcademicYearIds", [])
                  ) || null,
              },
            },
          });
        }
      } else {
        dispatch(
          setToastMsg({
            type: "warning",
            msg: "toastMsgs:something_went_wrong",
          })
        );
      }
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};

export const getShowStudentUIDInSchoolSetup = () =>
  ACLStore.can("FeatureFlag:ShowStudentUIDInSchoolSetup");

export const getStaffFeedForOrganizationFromCache = ({
  organizationId,
  filters,
  academicYear,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getTeacherFeedQuery,
      variables: {
        filters,
        organizationId,
        academicYear,
      },
    });
  } catch (e) {
    return {};
  }

  if (!queryData) return {};
  return _.get(queryData, "node", {});
};

// Reducer Handlers
const REDUCER_HANDLERS = {
  [CHANGE_SEARCH_TERM]: (state, action) => {
    return update(state, { searchText: { $set: action.payload } });
  },
  [INIT_SEARCH_TERM]: (state, action) => {
    return update(state, { searchText: { $set: initialState.searchText } });
  },
  [UPDATE_FILTERS]: (state, action) => {
    const params = action.data;
    Object.keys(params).map((key, index) => {
      state = update(state, {
        filters: {
          [key]: { $set: params[key] },
        },
      });
    });
    return state;
  },
  [UPDATE_BULK_ASSIGN_YEAR_GROUP_SEARCH_TEXT]: (state, action) => {
    return update(state, {
      bulkAssignYearGroupModal: {
        searchText: { $set: action.payload },
      },
    });
  },
  [UPDATE_BULK_ASSIGN_YEAR_GROUP_COURSES_FILTER]: (state, action) => {
    return update(state, {
      bulkAssignYearGroupModal: {
        filters: {
          courses: { $set: action.payload },
        },
      },
    });
  },
  [UPDATE_BULK_ASSIGN_YEAR_GROUP_ARCHIVAL_STATE_FILTER]: (state, action) => {
    return update(state, {
      bulkAssignYearGroupModal: {
        filters: {
          archivalState: { $set: action.payload },
        },
      },
    });
  },
  [SET_BASIC_DETAILS]: (state, action) => {
    return update(state, {
      basicDetails: { $set: action.payload },
    });
  },
  [SET_PERSONAL_DETAILS]: (state, action) => {
    return update(state, {
      personalDetails: { $set: action.payload },
    });
  },
  [SET_STUDENT_CLASS_MAPPING]: (state, action) => {
    return update(state, {
      studentClassMapping: { $set: action.payload },
    });
  },
  [RESET_MANAGE_MODAL]: state => {
    return update(state, {
      basicDetails: { $set: {} },
      personalDetails: { $set: {} },
      studentClassMapping: { $set: { classList: [] } },
    });
  },
  [SET_SELECTED_OR_EXCLUDED_STUDENTS]: (state, action) => {
    return update(state, {
      selectedOrExcludedStudents: { $set: action.payload },
    });
  },
  [SET_ARE_ALL_STUDENTS_SELECTED]: (state, action) => {
    return update(state, {
      areAllStudentsSelected: { $set: action.payload },
    });
  },
  [SET_BULK_SELECTED_STUDENT_STATUS_COUNTS]: (state, action) => {
    return update(state, {
      bulkSelectedStudentStatusCounts: { $set: action.payload },
    });
  },
  [RESET_BULK_ACTIONS_STATE]: state => {
    return update(state, {
      selectedOrExcludedStudents: {
        $set: initialState.selectedOrExcludedStudents,
      },
      areAllStudentsSelected: { $set: initialState.areAllStudentsSelected },
      bulkSelectedStudentStatusCounts: {
        $set: _.cloneDeep(initialState.bulkSelectedStudentStatusCounts),
      },
    });
  },
  [RESET_AFFECTED_BULK_ACTIONS_STATE]: (state, { payload: { studentIds } }) => {
    if (_.isEmpty(studentIds)) return state;

    return update(state, {
      selectedOrExcludedStudents: {
        $set: _.difference(state.selectedOrExcludedStudents, studentIds),
      },
    });
  },
  [SET_BULK_ACTIVITIES_POLLING_STATE]: (state, { payload: { isActive } }) => {
    return update(state, {
      isBulkActivitiesPollingActive: { $set: isActive },
    });
  },
};

// Initial State and export Reducer

const initialState = {
  selectedOrExcludedStudents: [],
  areAllStudentsSelected: false,
  bulkSelectedStudentStatusCounts: {
    blocked: 0,
    archived: 0,
    yearGroupToStudents: {},
  },
  isBulkActivitiesPollingActive: false,
  searchText: "",
  filters: {
    courses: [],
    showOnlyResultsWithNoAssignedClasses: null,
    selectedCurriculum: null,
    archivalState: "ACTIVE",
    yearGroups: [],
    blockStatus: "UNBLOCKED",
    schools: [],
    academicYears: [],
    selectedCurriculumId: null,
  },
  bulkAssignYearGroupModal: {
    searchText: "",
    filters: {
      courses: [],
      archivalState: "ALL",
    },
  },
  basicDetails: {},
  personalDetails: {},
  studentClassMapping: {
    classList: [],
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

export const getStudentsQueryVariablesWithoutSearch = baseStudentQueryVariables => ({
  ...baseStudentQueryVariables,
  filters: {
    ..._.get(baseStudentQueryVariables, "filters", {}),
    searchText: "",
  },
});
